<template>
  <div class="switch-sensitivity-setting-page">
    <div id="listWidget" class="mb-4">
    <!-- 단계별 안내 -->
    <div class="instruction-card card-item mb-2">
      <div class="instruction-header">
        <i class="fa-solid fa-list-ol me-2 text-primary"></i>
        <span class="instruction-title">스위치 감도 설정</span>
      </div>
      <div class="instruction-body">
        <div class="instruction-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <span>원하는 스위치 감도를 체크박스에 체크해주세요.</span>
          </div>
        </div>
        <div class="instruction-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <span>스위치 감도가 낮으면 스위치를 조금 더 오래 눌러야 작동합니다. 감도가 낮으면 스위치 노이즈에 강하고 스위치 반응 속도는 느리며, 감도가 높으면 노이즈에는 다소 약하나 스위치 반응 속도는 빠릅니다.</span>
          </div>
        </div>
        <div class="instruction-step">
          <div class="step-number">3</div>
          <div class="step-content">
            <span>변경 버튼을 눌러서 변경해 주세요.</span>
          </div>
        </div>
      </div>
    </div>

    <div class="sensitivity-list-card card-item mb-3" v-if="SensitivityList.length > 0">
      <div class="sensitivity-list-card-header">
        <i class="fa-solid fa-microchip me-2 text-primary"></i>
        <span class="sensitivity-list-title">스위치 감도 선택</span>
      </div>
      <div class="sensitivity-list-card-body">
        <div
          class="sensitivity-option-card"
          :class="{ 'sensitivity-option-card--selected': selectedSensitivity == item.value }"
          v-for="(item, index) in SensitivityList"
          :key="item.value"
          @click="onSelectSensitivityChange(item.value, index)"
        >
          <div class="form-check sensitivity-option-check">
            <input
              class="form-check-input sensitivity-option-radio"
              type="radio"
              :name="`sensitivityOption`"
              :id="`gridCheck${index}`"
              :value="item.value"
              :checked="selectedSensitivity == item.value"
              @change="() => onSelectSensitivityChange(item.value, index)"
            />
            <label class="form-check-label" :for="`gridCheck${index}`">{{ item.text }}</label>
          </div>
          <i class="fa-solid fa-check text-primary sensitivity-option-check-icon" v-if="selectedSensitivity == item.value"></i>
        </div>
      </div>
    </div>
    </div>

    <div id="footer" class="app-footer ms-0">
    <footer class="mt-auto">
      <div class="row">
        <div class="col" />
        <div class="col" />
        <div class="col">
          <button
            type="button"
            class="btn btn-primary btn-lg footer-btn"
            @click="onBatchSettingClick"
          >
            변경
          </button>
        </div>
      </div>
    </footer>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  getCurrentInstance,
  computed,
  onMounted,
  onBeforeUnmount,
  reactive,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import useMqttControl from '@/common/hooks/useMqttControl';
import { SensitivityList } from '@/common/constants';

const store = useStore();
const router = useRouter();
const { emitter, mobile, logger } =
  getCurrentInstance().appContext.config.globalProperties;
const mqttControl = reactive(useMqttControl());
const zone = computed(() => store.getters['home/getCurrentZone']);
const selectedSensitivity = ref(3);

onMounted(() => {
  if (zone.value) {
    selectedSensitivity.value = zone.value.sensitivity === undefined ? selectedSensitivity.value : zone.value.sensitivity;
  }
});

onBeforeUnmount(() => {
  store.dispatch('home/fetchAllZones');
});

const onBatchSettingClick = async () => {
  if (selectedSensitivity.value === undefined) {
    emitter.emit('showPopAlert', {
      description: '스위치 감도를 선택해주세요.',
    });
    return;
  }

  try {
    zone.value?.controllers?.forEach(controller => {
      mqttControl.onPublishController(controller._id, {
        serial: controller._id,
        cmd: 'ss',
        type: 1,
        data: {
          setting: selectedSensitivity.value
        }
      });
    });

    emitter.emit('showPopAlert', {
      description: "스위치 감도가 변경되었습니다.",
      okCallback: () => router.go(-1),
      cancelCallback: () => router.go(-1),
    });
  } catch (e) {
    emitter.emit('showPopAlert', {
      description: "스위치 감도 설정에 실패했습니다.",
    });
  }
};

const onSelectSensitivityChange = (value, index) => {
  selectedSensitivity.value = value;
};
</script>

<style scoped>
/* instruction-card 스타일 - FadeSettingPage와 동일 */
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.instruction-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.instruction-step:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0d6efd;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.75rem;
}

.step-content {
  flex: 1;
  padding-top: 0.1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #495057;
}

.sensitivity-list-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-top: 1rem;
}

.sensitivity-list-card-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.sensitivity-list-card-body {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.sensitivity-option-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 0.875rem 1rem;
  cursor: pointer;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.sensitivity-option-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.sensitivity-option-card--selected {
  border-color: #0d6efd;
  box-shadow: 0 2px 8px rgba(13, 110, 253, 0.15);
}

.sensitivity-option-radio {
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  pointer-events: none;
  margin: 0;
  width: 0;
  height: 0;
}

.sensitivity-option-check {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  flex: 1;
  cursor: pointer;
  padding-left: 0;
}

.sensitivity-option-check .form-check-label {
  cursor: pointer;
  font-size: 0.85rem;
  color: #212529;
  margin-left: 0.25rem;
}

.sensitivity-option-check-icon {
  flex-shrink: 0;
  font-size: 0.9rem;
}

.footer-btn {
  width: 100%;
  border-radius: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
</style>
